import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";

import "@fontsource/space-grotesk/500.css";
import "./styles/globals.css";
import { routeTree } from "./routeTree.gen";

// Stale SW after a deploy can hand out an old index.html that references new-hash
// chunks (or vice versa) and dynamic imports throw ChunkLoadError. Reload once
// to let the new SW (skipWaiting + clientsClaim) take over and serve a coherent
// asset set. Guarded by sessionStorage so a genuinely-broken state can't loop.
const CHUNK_RELOAD_KEY = "__chunk_reload_attempted";
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

function looksLikeChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  if ((reason as { name?: string }).name === "ChunkLoadError") return true;
  const message = typeof reason === "string"
    ? reason
    : (reason as { message?: string }).message ?? "";
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

window.addEventListener("unhandledrejection", (event) => {
  if (!looksLikeChunkLoadError(event.reason)) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

window.addEventListener("error", (event) => {
  // Synchronous chunk parse errors land here, not in unhandledrejection.
  if (!looksLikeChunkLoadError(event.error ?? event.message)) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

// Clear the guard on a clean load so the next stale-deploy event can recover.
window.addEventListener("load", () => {
  // Defer one tick so a chunk error during initial mount can still trigger reload
  // before this clears the flag.
  queueMicrotask(() => {
    setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 5000);
  });
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root missing from index.html");

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
